/*───────────────────────────────────────────────────────────────
 📄 File: components/ui/RecordingTtsModeSection.tsx
 🧩 Description:
    TTS mode section for RecordingTtsModal.
    Renders AI profile selector, silence padding inputs,
    text area, and Generate/Play/Download action row.
────────────────────────────────────────────────────────────────*/

import { Bot, Download, Loader2, Play } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import Select from './select';
import type { AiProfile } from '../../modules/recordingManagement/types';

interface TtsHook {
    isGeneratingTts: boolean;
    ttsPreview: string | null;
    generateTTS: (text: string, profile: AiProfile | null, onGenerated: (url: string) => void) => void;
}

interface Props {
    ttsText: string;
    ttsProfiles: AiProfile[];
    selectedAiProfile: AiProfile | null;
    startSilenceMs: number;
    endSilenceMs: number;
    currentAudioUrl?: string;
    recordingName: string;
    tts: TtsHook;
    onTtsTextChange: (v: string) => void;
    onProfileChange: (profile: AiProfile | null) => void;
    onStartSilenceChange: (v: number) => void;
    onEndSilenceChange: (v: number) => void;
    onAudioGenerated: (url: string) => void;
    onPlay: () => void;
}

export function RecordingTtsModeSection({
    ttsText, ttsProfiles, selectedAiProfile, startSilenceMs, endSilenceMs,
    currentAudioUrl, recordingName, tts,
    onTtsTextChange, onProfileChange, onStartSilenceChange, onEndSilenceChange,
    onAudioGenerated, onPlay,
}: Props) {
    const clampMs = (v: number) => Math.max(0, Math.min(500, v));

    return (
        <div className="space-y-3">
            {/* AI Profile */}
            <div className="space-y-1">
                <Label htmlFor="rm-profile">AI Profile</Label>
                <Select
                    value={selectedAiProfile?.id?.toString() || ''}
                    onValueChange={(val) => {
                        const p = ttsProfiles.find((x) => x.id.toString() === val);
                        onProfileChange(p || null);
                    }}
                    options={ttsProfiles.map((p) => ({ value: p.id.toString(), label: `${p.profile_name} (${p.provider_name})` }))}
                    placeholder="Select AI profile..."
                />
            </div>

            {/* Silence Padding */}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label htmlFor="rm-start-silence">Start Silence (ms)</Label>
                    <Input
                        id="rm-start-silence" type="number" min={0} max={500} step={10}
                        value={startSilenceMs}
                        onChange={(e) => onStartSilenceChange(clampMs(Number(e.target.value)))}
                    />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="rm-end-silence">End Silence (ms)</Label>
                    <Input
                        id="rm-end-silence" type="number" min={0} max={500} step={10}
                        value={endSilenceMs}
                        onChange={(e) => onEndSilenceChange(clampMs(Number(e.target.value)))}
                    />
                </div>
            </div>

            {/* TTS text */}
            <div className="space-y-1">
                <Label htmlFor="rm-text" required>Text to convert</Label>
                <textarea
                    id="rm-text" value={ttsText} onChange={(e) => onTtsTextChange(e.target.value)}
                    placeholder="Type the text that will be converted to speech..."
                    className="w-full min-h-[100px] px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y bg-background text-foreground"
                    rows={4}
                />
            </div>

            {/* Generate / Play / Download */}
            <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <Button
                    type="button" variant="default" size="sm"
                    onClick={() => tts.generateTTS(ttsText, selectedAiProfile, onAudioGenerated)}
                    disabled={!selectedAiProfile || !ttsText.trim() || tts.isGeneratingTts}
                    className="flex-1 flex items-center gap-2 justify-center"
                >
                    {tts.isGeneratingTts ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : <><Bot className="h-4 w-4" /> Generate Preview</>}
                </Button>
                <Button
                    type="button" variant="outline" size="sm" onClick={onPlay}
                    disabled={!currentAudioUrl || tts.isGeneratingTts}
                    className="flex items-center gap-2 border-green-500 text-green-600 hover:bg-green-500/10"
                >
                    <Play className="h-4 w-4" /> Play
                </Button>
                <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => {
                        if (currentAudioUrl) {
                            const a = document.createElement('a');
                            a.href = currentAudioUrl;
                            a.download = `${recordingName || 'recording'}.wav`;
                            a.click();
                        }
                    }}
                    disabled={!currentAudioUrl || tts.isGeneratingTts}
                    className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-500/10"
                >
                    <Download className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}
